import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TICKET_CATEGORIES, TICKET_PRIORITIES, can } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { dayInTz, timeInTz } from '../../lib/format';
import { useSession } from '../session';
import { Sheet } from '../ui/Sheet';
import { Button } from '../ui/Button';
import { Notice } from '../ui/Badge';
import { Skeleton } from '../ui/Skeleton';
import { InlineRename } from '../ui/InlineRename';
import { isNotFound, refreshUntilShown } from '../ui/QueryStates';
import { PhotoGrid } from '../photos/PhotoGrid';
import {
  ReplyDue,
  TicketPriorityBadge,
  TicketStatusBadge,
  ticketName,
} from './TicketBits';
import {
  DELIVERY_POLL_MS,
  categoryLabel,
  deliveryPending,
  historyLine,
  inputOfInstant,
  instantOfInput,
  mayOwnTickets,
  mayResolveFrom,
  priorityLabel,
  raisedBy,
  resolveMissing,
  rootCauseLabel,
  transitionMoves,
} from './ticketView';
import { useTapOnce } from '../ui/useTapOnce';
import { sendOnce } from './sendOnce';
import { FollowButton } from './FollowButton';
import { ReplyDueEditor } from './ReplyDueEditor';
import { ResolveForm } from './ResolveForm';
import { Messages } from './Messages';
import { FactSelect } from './FactSelect';
import { OwnerPicker } from './OwnerPicker';
import type { Member, TicketDetail } from './types';

/**
 * The floating detail panel (spec §5, prototype 204 R): the shared `Sheet` (the
 * `JobPreviewPanel` pattern), opened from a List row, a Board card or a `?ticket=` link. It
 * fetches its own ticket (`ticket.get`, one call for everything it shows), so a deep link
 * opens it with nothing else on the page.
 *
 * ⚠️ The body (`TicketPanelBody`) is its own component: a Radix portal never mounts in the
 * node harness (LESSONS 2026-10-05), and the body is what the tests press.
 */
export function TicketPanel({
  ticketId,
  tz,
  now,
  members,
  onClose,
}: {
  /** The open ticket, or null for a closed panel. */
  ticketId: string | null;
  tz: string;
  now: number;
  members: readonly Member[];
  onClose: () => void;
}) {
  const trpc = useTRPC();
  const q = useQuery({
    ...trpc.ticket.get.queryOptions({ ticketId: ticketId ?? '' }),
    enabled: ticketId !== null,
    // A message's email reads "Sending…" until the drain answers: ask again while one does.
    refetchInterval: (query) =>
      deliveryPending(query.state.data) ? DELIVERY_POLL_MS : false,
  });
  const t = q.data?.ticket;
  return (
    <Sheet
      open={ticketId !== null}
      onClose={onClose}
      wide
      title={t ? ticketName(t) : 'Ticket'}
      badge={t ? <TicketStatusBadge status={t.status} /> : null}
      actions={
        t && q.data ? <FollowButton ticketId={t.id} following={q.data.following} /> : null
      }
    >
      {ticketId === null ? null : q.isPending ? (
        <div className="space-y-3">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : q.isError ? (
        isNotFound(q.error) ? (
          <p className="text-sm text-muted-foreground">
            That ticket isn&apos;t here. It was deleted, or the link is older than the
            ticket. Nothing is broken.
          </p>
        ) : (
          <p className="text-sm text-[var(--sw-danger-text)]">
            Couldn&apos;t load this ticket.{' '}
            <button type="button" className="underline" onClick={() => void q.refetch()}>
              Try again
            </button>
          </p>
        )
      ) : (
        <TicketPanelBody detail={q.data} tz={tz} now={now} members={members} />
      )}
    </Sheet>
  );
}

/**
 * Has the focus gone nowhere: to <body>, to a node that has left the page, or to the panel
 * itself (where the dialog's focus trap puts it when the focused button is removed)?
 */
function focusWasLost(): boolean {
  if (typeof document === 'undefined') return false;
  const el = document.activeElement as HTMLElement | null;
  return (
    !el || el === document.body || !el.isConnected || el.getAttribute('role') === 'dialog'
  );
}

/**
 * Everything the panel shows of one ticket: its facts (editable by whoever triages), its
 * status and the moves it can make, the Resolve form, the conversation, its photos and its
 * history. Every write goes out under its own tap lock and keeps its control shut until the
 * ticket on screen shows its outcome (`refreshUntilShown`).
 */
export function TicketPanelBody({
  detail,
  tz,
  now,
  members,
}: {
  detail: TicketDetail;
  tz: string;
  now: number;
  members: readonly Member[];
}) {
  const t = detail.ticket;
  const ticketId = t.id;
  const trpc = useTRPC();
  const qc = useQueryClient();
  const me = useSession();
  const canTriage = can(me.role, 'ticket', 'triage');
  const canAssign = can(me.role, 'ticket', 'assign');
  const canResolve = can(me.role, 'ticket', 'resolve');
  const canMessage = can(me.role, 'ticket', 'update');

  const [mode, setMode] = useState<'view' | 'resolving'>('view');
  const statusHeading = useRef<HTMLHeadingElement>(null);
  const resolveButton = useRef<HTMLButtonElement>(null);
  const shows = { queryKey: trpc.ticket.get.queryKey({ ticketId }) };

  const updateLock = useTapOnce();
  const assignLock = useTapOnce();
  const moveLock = useTapOnce();
  const resolveLock = useTapOnce();

  const update = useMutation(
    trpc.ticket.update.mutationOptions({
      onSuccess: () => refreshUntilShown(qc, shows),
      onSettled: () => updateLock.release(),
    }),
  );
  const assign = useMutation(
    trpc.ticket.assign.mutationOptions({
      onSuccess: () => refreshUntilShown(qc, shows),
      onSettled: () => assignLock.release(),
    }),
  );
  // A move changes which buttons the status control draws, so the pressed one is gone: the
  // focus goes to the status heading, not to <body> (LESSONS 2026-10-05). Only when it WAS
  // lost or still on the pressed button: someone who has moved on while the write was out
  // (pressed Resolve…, typed a message) keeps the focus where they put it.
  const movePressed = useRef<Element | null>(null);
  const move = useMutation(
    trpc.ticket.transition.mutationOptions({
      onSuccess: async () => {
        await refreshUntilShown(qc, shows);
        const at = typeof document === 'undefined' ? null : document.activeElement;
        if (at === movePressed.current || focusWasLost()) statusHeading.current?.focus();
      },
      onSettled: () => moveLock.release(),
    }),
  );
  const resolve = useMutation(
    trpc.ticket.resolve.mutationOptions({
      onSuccess: async () => {
        await refreshUntilShown(qc, shows);
        setMode('view');
      },
      onSettled: () => resolveLock.release(),
    }),
  );

  // Leaving the Resolve form hands the focus back: to "Resolve…" after a cancel, or to the
  // status heading once it is resolved and that button has gone.
  const lastMode = useRef(mode);
  useEffect(() => {
    if (lastMode.current === 'resolving' && mode === 'view') {
      (resolveButton.current ?? statusHeading.current)?.focus();
    }
    lastMode.current = mode;
  }, [mode]);

  const owners = members.filter((m) => mayOwnTickets(m.role));
  const nameOf = (id: string) => members.find((m) => m.userId === id)?.name ?? null;
  const moves = transitionMoves(t.status);
  const writeError = update.error ?? assign.error ?? move.error;

  return (
    <div className="space-y-6 text-sm">
      <section aria-labelledby={`${ticketId}-facts`} className="space-y-3">
        <h3 id={`${ticketId}-facts`} className="sr-only">
          Details
        </h3>
        {writeError ? (
          <Notice tone="danger" alert>
            {writeError.message}
          </Notice>
        ) : null}
        <dl className="space-y-3">
          <Fact label="Title">
            <span className="flex min-w-0 items-center justify-end gap-2">
              <span className="min-w-0 break-words font-medium">{t.title}</span>
              {canTriage ? (
                <InlineRename
                  value={t.title}
                  busy={update.isPending}
                  onSave={(title) =>
                    sendOnce(updateLock, update.mutate, { ticketId, title })
                  }
                />
              ) : null}
            </span>
          </Fact>
          <Fact label="Customer">
            <Link
              to="/customers/$customerId"
              params={{ customerId: t.customer.id }}
              className="font-medium underline-offset-2 hover:underline"
            >
              {t.customer.displayName}
            </Link>
          </Fact>
          <Fact label="Job">
            {t.job ? (
              <Link
                to="/jobs/$jobId"
                params={{ jobId: t.job.id }}
                className="font-medium underline-offset-2 hover:underline"
              >
                #{t.job.jobNumber} {t.job.title}
              </Link>
            ) : (
              <span className="text-muted-foreground">No job</span>
            )}
          </Fact>
          <Fact label="Category" htmlFor={`${ticketId}-category`}>
            {canTriage ? (
              <FactSelect
                id={`${ticketId}-category`}
                value={t.category}
                options={TICKET_CATEGORIES.map((c) => ({
                  value: c,
                  label: categoryLabel(c),
                }))}
                onSave={(category) =>
                  sendOnce(updateLock, update.mutate, { ticketId, category })
                }
              />
            ) : (
              categoryLabel(t.category)
            )}
          </Fact>
          <Fact label="Priority" htmlFor={`${ticketId}-priority`}>
            {canTriage ? (
              <FactSelect
                id={`${ticketId}-priority`}
                value={t.priority}
                options={TICKET_PRIORITIES.map((p) => ({
                  value: p,
                  label: priorityLabel(p),
                }))}
                onSave={(priority) =>
                  sendOnce(updateLock, update.mutate, { ticketId, priority })
                }
              />
            ) : (
              <TicketPriorityBadge priority={t.priority} />
            )}
          </Fact>
          <Fact label="Owner" htmlFor={`${ticketId}-owner`}>
            {canAssign ? (
              <OwnerPicker
                id={`${ticketId}-owner`}
                owner={t.owner}
                owners={owners}
                me={me.userId}
                canTake={mayOwnTickets(me.role)}
                busy={assign.isPending}
                onAssign={(ownerId) =>
                  sendOnce(assignLock, assign.mutate, { ticketId, ownerId })
                }
              />
            ) : t.owner ? (
              (t.owner.name ?? 'Former staff')
            ) : (
              'Nobody yet'
            )}
          </Fact>
          <Fact label="Reply due" htmlFor={`${ticketId}-due`}>
            {canTriage ? (
              <ReplyDueEditor
                // A new reply-due from the server (this panel's save, or a colleague's) is a
                // new draft; nothing is mirrored by an effect.
                key={t.replyDueAt ? new Date(t.replyDueAt).getTime() : 'none'}
                id={`${ticketId}-due`}
                value={t.replyDueAt}
                tz={tz}
                busy={update.isPending}
                onSave={(replyDueAt) =>
                  sendOnce(updateLock, update.mutate, { ticketId, replyDueAt })
                }
              />
            ) : null}
            <span className="block">
              <ReplyDue ticket={t} tz={tz} now={now} />
            </span>
          </Fact>
          <Fact label="Raised">
            {raisedBy(t)}, {dayInTz(t.createdAt, tz)}
          </Fact>
        </dl>
      </section>

      <section aria-labelledby={`${ticketId}-status`} className="space-y-3">
        <h3
          id={`${ticketId}-status`}
          ref={statusHeading}
          tabIndex={-1}
          className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
        >
          Status
        </h3>
        {t.status === 'resolved' ? (
          <div className="space-y-2 rounded-[var(--radius)] border border-border p-3">
            <p>
              <span className="text-muted-foreground">What fixed it: </span>
              <span className="whitespace-pre-wrap">{t.resolution}</span>
            </p>
            <p>
              <span className="text-muted-foreground">Root cause: </span>
              {t.rootCause ? rootCauseLabel(t.rootCause) : '—'}
            </p>
          </div>
        ) : null}
        {mode === 'resolving' ? (
          <ResolveForm
            pending={resolve.isPending}
            error={resolve.error?.message ?? null}
            onCancel={() => setMode('view')}
            onResolve={(v) => sendOnce(resolveLock, resolve.mutate, { ticketId, ...v })}
          />
        ) : canTriage || canResolve ? (
          <div className="flex flex-wrap gap-2">
            {canTriage
              ? moves.map((m) => (
                  <Button
                    key={m.to}
                    variant="ghost"
                    aria-disabled={move.isPending || undefined}
                    onClick={() => {
                      movePressed.current = document.activeElement;
                      sendOnce(moveLock, move.mutate, { ticketId, to: m.to });
                    }}
                  >
                    {m.label}
                  </Button>
                ))
              : null}
            {canResolve && mayResolveFrom(t.status) ? (
              <Button ref={resolveButton} onClick={() => setMode('resolving')}>
                Resolve…
              </Button>
            ) : null}
          </div>
        ) : null}
        {t.status === 'new' ? (
          <p className="text-xs text-muted-foreground">
            A new ticket opens when someone owns it.
          </p>
        ) : null}
      </section>

      <Messages detail={detail} tz={tz} canMessage={canMessage} />

      <PhotoGrid entityType="ticket" entityId={ticketId} title="Photos" compact />

      <section aria-labelledby={`${ticketId}-history`} className="space-y-2">
        <h3
          id={`${ticketId}-history`}
          className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
        >
          History
        </h3>
        <ol className="space-y-1">
          {detail.history.map((e) => (
            <li
              key={e.id}
              className="flex flex-wrap items-baseline justify-between gap-x-3"
            >
              <span>{historyLine(e, nameOf, t.source)}</span>
              <span className="text-xs text-muted-foreground">
                {dayInTz(e.at, tz)} {timeInTz(e.at, tz)}
              </span>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

/** A fact's label and its value or control, the label tied to the control when there is one. */
function Fact({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
      <dt className="shrink-0 text-muted-foreground">
        {htmlFor ? <label htmlFor={htmlFor}>{label}</label> : label}
      </dt>
      <dd className="min-w-0 break-words sm:text-right">{children}</dd>
    </div>
  );
}
